import React, { useEffect } from 'react';
import { VaBreadcrumbs } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { useFeatureToggle } from '~/platform/utilities/feature-toggles/useFeatureToggle';
import { formatDateParsedZoneLong } from 'platform/utilities/date/index';
import { useLoaderData, redirect } from 'react-router-dom';
import SubmissionsWrapper from '../components/SubmissionsWrapper';
import { inProgressFormsBC } from '../utilities/poaRequests';
import SortForm from '../components/SortForm';
import KeyValueList from '../components/KeyValueList';
import {
  SEARCH_PARAMS,
  SORT_BY,
  SORT_DEFAULTS,
  SUBMISSIONS_PATH,
  SUBMIT_FORMS_PATH,
} from '../utilities/constants';
import api from '../utilities/api';

const InProgressFormCard = inProgressFormData => {
  const {
    veteranLastName,
    veteranFirstName,
    formId,
    expiryDate,
    updatedAt,
    claimantId,
  } = { ...inProgressFormData };
  const veteranName = `${veteranLastName}, ${veteranFirstName}`;
  const cardData = {
    'Form expires on': formatDateParsedZoneLong(expiryDate),
    'Last saved': formatDateParsedZoneLong(updatedAt),
  };
  const formUrlSegment =
    formId === '21-686c-ARP'
      ? 'va-form-21-686c-674'
      : `submit-va-form-${formId}`;

  const visibleFormLabel =
    formId === '21-686c-ARP' ? '21-686c and 21-674' : formId;

  return (
    <li>
      <va-card>
        <h3 className="vads-u-display--flex vads-u-flex-direction--column vads-u-margin-top--0">
          <span className="vads-u-font-size--h3">{veteranName}</span>
          <span className="vads-u-font-size--h5">
            VA Form {visibleFormLabel}
          </span>
        </h3>
        <KeyValueList data={cardData} />

        <va-link
          active
          class="vads-u-margin-top--2 vads-u-display--block"
          label={`Continue VA Form ${visibleFormLabel} for ${veteranName}`}
          href={`/representative/representative-form-upload/${formUrlSegment}?claimantId=${claimantId}`}
          text={`Continue VA Form ${visibleFormLabel}`}
        />
      </va-card>
    </li>
  );
};

const InProgressFormsPage = title => {
  useEffect(() => {
    document.title = title.title;
  }, [title]);

  const { TOGGLE_NAMES, useToggleValue } = useFeatureToggle();
  const isIPFEnabled = useToggleValue(
    TOGGLE_NAMES.accreditedRepresentativePortalSubmit686cV2,
  );
  const isSubmissionsUpdateEnabled = useToggleValue(
    TOGGLE_NAMES.accreditedRepresentativePortalCdUpdateMain,
  );

  const inProgressForms = useLoaderData() || [];

  return (
    <section className="vads-u-width--full claimant-details-submission-history ipf-page">
      <div className="claimant-details-submission-history__breadcrumbs">
        <VaBreadcrumbs
          breadcrumbList={inProgressFormsBC}
          homeVeteransAffairs={false}
          wrapping
        />
      </div>

      <SubmissionsWrapper>
        <h2
          data-testid="submission-history-page-heading"
          className="vads-u-margin-y--0"
        >
          In-progress forms
        </h2>
        <p className="va-introtext vads-u-margin-y--0 vads-u-margin-bottom--2">
          Continue your in-progress online forms.
        </p>
        {isIPFEnabled ? (
          <>
            <SortForm
              options={[
                {
                  sort: 'newest',
                  label: 'Newest',
                },
                {
                  sort: 'oldest',
                  label: 'Oldest',
                },
              ]}
              defaults={SORT_DEFAULTS}
              showCheckbox={false}
            />
            <ul>
              {inProgressForms.map((inProgressForm, index) => {
                return <InProgressFormCard {...inProgressForm} key={index} />;
              })}
            </ul>
          </>
        ) : (
          <div>
            <p>This page isn’t available yet.</p>
            <va-link-action
              href={
                isSubmissionsUpdateEnabled
                  ? SUBMIT_FORMS_PATH
                  : SUBMISSIONS_PATH
              }
              text="Submit forms"
              type="primary"
            />
          </div>
        )}
      </SubmissionsWrapper>
    </section>
  );
};

InProgressFormsPage.loader = async ({ request }) => {
  const { searchParams } = new URL(request.url);
  const sort = searchParams.get(SEARCH_PARAMS.SORT);
  if (!Object.values(SORT_BY).includes(sort)) {
    searchParams.set(SEARCH_PARAMS.SORT, SORT_BY.NEWEST);
    throw redirect(`?${searchParams}`);
  }
  // Wait for the Promise-based Response object
  try {
    const response = await api.getInProgressForms(searchParams);
    // Returns the actual response so we can grab the data with the `useLoaderData()` hook
    return response?.json();
  } catch (err) {
    // returns 404 if IPF is disabled; catch to display link to submissions page
    if (err instanceof Response && err.status === 404) {
      return [];
    }
    throw err;
  }
};

export default InProgressFormsPage;
